import { Dialog } from "primereact/dialog";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import "./styles/headerPopupPanel.scss";
import { useWindowSize } from "react-use";
import { routes } from "../../constants/routes";
import { useNavigate, useLocation } from "react-router-dom";
import { SIDEBAR_TOGGLE } from "../../constants/queryKeys";
import { checkPermission, setSidebarToggle } from "../../helpers/utils";
import { ResetActiveMenu } from "../../helpers/withSidebarHelper";
import { useQueryClient } from "react-query";
import { PermissionKeys } from "../../constants/constants";

type Props = {
    visible: boolean;
    setVisible: Dispatch<SetStateAction<boolean>>;
};

const menuList = [
    {
        label: "Dashboard",
        icon: "ri-dashboard-3-line",
        activeMenu: "dashboard",
        adminPath: routes.dashboard,
        staffPath: routes.dashboard,
        permission: [],
    },
    {
        label: "Users & Permission",
        icon: "ri-user-settings-line",
        activeMenu: "collapseUserManagement",
        adminPath: routes.manage_users,
        staffPath: null,
        permission: [
            PermissionKeys.MANAGE_USERS,
            PermissionKeys.MANAGE_ROLES,
            PermissionKeys.MANAGE_PERMISSIONS,
        ],
    },
    {
        label: "F&B Menu",
        icon: "ri-bowl-line",
        activeMenu: "fbmenu",
        adminPath: routes.menu,
        staffPath: null,
        permission: [
            PermissionKeys.MANAGE_MENU,
            PermissionKeys.MANAGE_CUSTOM_FNB_MENU,
            PermissionKeys.MANAGE_ADDONS,
            PermissionKeys.MANAGE_ADDON_GROUPS,
            PermissionKeys.MANAGE_INGREDIENTS,
        ],
    },
    {
        label: "Guest Satisfaction",
        icon: "ri-question-answer-line",
        activeMenu: "collapseGuestManagement",
        adminPath: routes.survey,
        staffPath: null,
        permission: [
            PermissionKeys.MANAGE_SURVEY,
            PermissionKeys.MANAGE_GAME,
            PermissionKeys.MANAGE_COUPON,
        ],
    },
    {
        label: "Guest Info",
        icon: "ri-user-star-line",
        activeMenu: "guestInfo",
        adminPath: routes.guest_info,
        staffPath: null,
        permission: [PermissionKeys.MANAGE_GUEST_INFO],
    },
    {
        label: "Scheduling",
        icon: "ri-calendar-event-line",
        activeMenu: "scheduleManagement",
        adminPath: routes.manage_work_schedule,
        staffPath: routes.staff_schedule,
        permission: [
            PermissionKeys.MANAGE_WORK_TIME_SCHEDULE,
        ],
    },
    {
        label: "News & Events",
        icon: "ri-user-star-line",
        activeMenu: "newsAndEvents",
        adminPath: routes.news_and_events_management,
        staffPath: null,
        permission: [PermissionKeys.MANAGE_NEWS_AND_EVENTS],
    },
    {
        label: "File Operations",
        icon: "ri-file-line",
        activeMenu: "collapseFileManagement",
        adminPath: routes.file_management,
        staffPath: null,
        permission: [PermissionKeys.MANAGE_FILES],
    },
    {
        label: "Reports",
        icon: "ri-file-chart-line",
        activeMenu: "reports",
        adminPath: routes.survey_report,
        staffPath: null,
        permission: [PermissionKeys.MANAGE_REPORTS],
    },
    {
        label: "Settings",
        icon: "ri-settings-5-line",
        activeMenu: "settings",
        adminPath: routes.settings,
        staffPath: null,
        permission: [
            PermissionKeys.MANAGE_BRANDING,
            PermissionKeys.MANAGE_WELCOME_SCREEN,
            PermissionKeys.MANAGE_ABOUT_US,
            PermissionKeys.MANAGE_GENERAL_SETTINGS,
        ],
    },
    {
        label: "My Profile",
        icon: "ri-user-settings-line",
        activeMenu: "profile",
        adminPath: routes.profile,
        staffPath: routes.profile,
        permission: [],
    },
    {
        label: "Help",
        icon: "ri-questionnaire-line",
        activeMenu: "help",
        adminPath: routes.help,
        staffPath: routes.help,
        permission: [],
    },
];

function HeaderPopupPanel({ visible, setVisible }: Readonly<Props>) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const { width: windowWidth } = useWindowSize();

    // state variables
    const [activeMenu, setActiveMenu] = useState<string | null>("");

    const menuClicked = (menuItem: string) =>
        localStorage.setItem("activeMenu", menuItem);

    const checkPermissions = (permissions: string[]) => {
        return permissions.some((permission) =>
            checkPermission(queryClient, permission)
        );
    };

    // effect to set the active menu item
    useEffect(() => {
        setActiveMenu(localStorage.getItem("activeMenu"));
        // open and close left panel based on screen size
        if (window.innerWidth <= 1180.98) {
            queryClient.setQueryData(SIDEBAR_TOGGLE, setSidebarToggle(true));
        } else {
            queryClient.setQueryData(SIDEBAR_TOGGLE, setSidebarToggle(false));
        }
        // reset activeMenu on page unmount
        return ResetActiveMenu(location, menuClicked, setActiveMenu);
    }, [location, queryClient]);

    return (
        <Dialog
            id={`custom-popup-header-menu`}
            key={`custom-popup-header-menu`}
            visible={visible}
            modal={true}
            draggable={false}
            position="top-left"
            closeOnEscape={true}
            closable={true}
            onHide={() => setVisible(false)}
            className="bg-white position-relative"
            style={{
                width: "42rem",
                marginLeft: windowWidth <= 641 ? "0.5rem" : "1.5rem",
                marginTop: "3.5rem",
            }}
            breakpoints={{ "641px": "100vw" }}
            showHeader={false}
            pt={{
                content: {
                    style: {
                        borderTopLeftRadius: "6px",
                        borderTopRightRadius: "6px",
                        padding: "1rem",
                        boxShadow: "0 1rem 3rem rgba(0, 0, 0, .175)"
                    },
                },
            }}
            resizable={false}
            dismissableMask={true}
            maskClassName="bg-transparent"
        >
            <div className="row custom-popup-header-menu">
                <span
                    style={{
                        position: "absolute",
                        backgroundColor: "var(--primary-color)",
                        width: "26px",
                        height: "25px",
                        borderRadius: "50%",
                        top: "-10px",
                        right: "-10px",
                        padding: "2px 0px 0px 3.7px",
                        display: windowWidth <= 600 ? "" : "none",
                    }}
                >
                    <i
                        className="ri-close-line cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => setVisible(false)}
                        onKeyDown={(e) => e.key === "Enter" && setVisible(false)}
                        style={{ fontSize: "1.35rem", fontWeight: "bold", color: "white" }}
                    />
                </span>
                {menuList.map((item, index) => {
                    let path: string | null = item.adminPath;
                    if (item.permission.length > 0) {
                        const hasPermission = checkPermissions(item.permission);
                        if (!hasPermission) {
                            path = item.staffPath;
                        }
                        // console.log('path', path, hasPermission);
                    }

                    if (!path) return null;

                    let colClass = '';
                    if (windowWidth <= 370) {
                        colClass = 'col-5';
                    } else if (windowWidth <= 535) {
                        colClass = 'col-4';
                    } else {
                        colClass = 'col-3';
                    }

                    return (
                        <div
                            key={`popup-header-menu-${index + 1}`}
                            className={`${colClass} my-4 pointer popup-header-menu-item ${activeMenu === item.activeMenu ? "active" : ""
                                }`}
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                                e.preventDefault();
                                menuClicked(item.activeMenu);
                                setVisible(false);
                                navigate(path);
                            }}
                        >
                            <div className="d-flex flex-column align-items-center justify-content-center">
                                <i className={`${item.icon} popup-header-menu-icon`} />
                                <span
                                    className="popup-header-menu-label"
                                    style={{
                                        fontSize: "0.95rem",
                                        letterSpacing: "0.15px",
                                        textAlign: "center",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {item.label}
                                </span>
                            </div>
                        </div>
                    )
                })}
            </div>
        </Dialog>
    );
}

export default HeaderPopupPanel;
